import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Divider,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowDropDown as ArrowDropDownIcon,
  CloudSync as SyncIcon,
  Search as SearchIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import { APIProvider, AdvancedMarker, Map as GoogleMap, Marker, Pin } from '@vis.gl/react-google-maps';
import { useAuth } from '../../contexts/AuthContext';
import ProjectContextList from './ProjectContextList';
import ProjectContextForm from './ProjectContextForm';
import DocumentList from './DocumentList';
import DocumentUpload from './DocumentUpload';
import DocumentPreview from './DocumentPreview';
import { LocationPickerDialog } from './LocationPickerDialog';
import projectService, {
  ProjectContext,
  ProjectDocument,
  ProjectMetadata,
  ProjectMetadataUpdate,
  ProjectSummary,
  ProjectContextFilters,
  ProjectDocumentFilters,
  ProjectAddressPreview,
  CONTEXT_TYPES
} from '../../services/projectService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`project-tabpanel-${index}`}
      aria-labelledby={`project-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

interface ProjectCardData {
  project_code: string;
  project_name?: string;
  availability_status?: string;
  currency?: string;
  context_count: number;
  document_count: number;
  last_updated?: string;
  metadata_updated_at?: string;
}

interface ProjectDetailsFormState {
  project_name: string;
  status: string;
  developer: string;
  contact: string;
  hubspot_virtual_meeting_slug: string;
  hubspot_virtual_scheduling_enabled: boolean;
  virtual_advisor_timezone: string;
  plus_code: string;
  link: string;
  pdf_link: string;
  availability_status: string;
  currency: string;
  location: string;
  lat: string;
  lng: string;
}

const Projects: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseTime = (value?: string) => {
    if (!value) return 0;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : 0;
  };
  
  // Projects (metadata + optional stats)
  const [projectSummaries, setProjectSummaries] = useState<ProjectCardData[]>([]);
  const [selectedProjectCode, setSelectedProjectCode] = useState<string>('');
  // all projects metadata (used for map)
  const [allProjects, setAllProjects] = useState<ProjectMetadata[]>([]);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const googleMapRef = useRef<any | null>(null);
  const markersRef = useRef<any[]>([]);
  const infoWindowRef = useRef<any | null>(null);

  const escapeHtml = (unsafe: string) => {
    return String(unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const recentProjects = useMemo(() => {
    return [...projectSummaries]
      .sort((a, b) => {
        const aActivity = Math.max(parseTime(a.last_updated), parseTime(a.metadata_updated_at));
        const bActivity = Math.max(parseTime(b.last_updated), parseTime(b.metadata_updated_at));
        return bActivity - aActivity;
      })
      .slice(0, 4);
  }, [projectSummaries]);

  const projectDropdownOptions = useMemo(() => {
    return [...projectSummaries].sort((a, b) => {
      const aKey = a.project_name!.toLocaleLowerCase();
      const bKey = b.project_name!.toLocaleLowerCase();
      return aKey.localeCompare(bKey, undefined, { sensitivity: 'base' });
    });
  }, [projectSummaries]);
  
  // Context management
  const [contextFormOpen, setContextFormOpen] = useState(false);
  const [editingContext, setEditingContext] = useState<ProjectContext | null>(null);
  const [contextFilters, setContextFilters] = useState<ProjectContextFilters>({});
  
  // Document management
  const [documentFilters, setDocumentFilters] = useState<ProjectDocumentFilters>({});
  const [previewDocument, setPreviewDocument] = useState<ProjectDocument | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  // Header actions menu (split button)
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [vectorActionLoading, setVectorActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Unsaved changes protection (Detalles tab)
  const detailsInitialRef = useRef<ProjectDetailsFormState | null>(null);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [pendingTabValue, setPendingTabValue] = useState<number | null>(null);
  const [pendingProjectCode, setPendingProjectCode] = useState<string | null>(null);

  // Project details editing
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSuccess, setDetailsSuccess] = useState<string | null>(null);
  const [detailsForm, setDetailsForm] = useState<ProjectDetailsFormState>({
    project_name: '',
    status: 'active',
    developer: 'Arquitectura y Concreto',
    contact: '',
    hubspot_virtual_meeting_slug: '',
    hubspot_virtual_scheduling_enabled: true,
    virtual_advisor_timezone: 'America/Bogota',
    plus_code: '',
    link: '',
    pdf_link: '',
    availability_status: '',
    currency: 'COP',
    location: '',
    lat: '',
    lng: '',
  });
  const [addressPreviewMessage, setAddressPreviewMessage] = useState<string | null>(null);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const pdfUploadInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  useEffect(() => {
    if (!selectedProjectCode && tabValue !== 0) {
      setTabValue(0);
    }
  }, [selectedProjectCode, tabValue]);

  useEffect(() => {
    if (tabValue !== 0) return;
    if (!selectedProjectCode) return;
    loadProjectDetails(selectedProjectCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, selectedProjectCode]);

  const formatAxiosError = (err: any, fallbackMessage: string) => {
    if (err?.response) {
      const status = err.response.status;
      const body = err.response.data;
      const detail = body?.detail || (typeof body === 'string' ? body : JSON.stringify(body));
      return `${fallbackMessage} (${status}): ${detail}`;
    }
    if (err?.request) {
      return 'Error al conectar con la API: sin respuesta del servidor';
    }
    return err?.message || fallbackMessage;
  };

	  const loadProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      let projectsValue: any = null;
      let projectsError: any = null;
      let summaries: ProjectSummary[] = [];
      let summariesError: any = null;

      await Promise.all([
        projectService
          .getProjects(0, 1000)
          .then((value) => {
            projectsValue = value;
          })
          .catch((err) => {
            projectsError = err;
          }),
        projectService
          .getProjectSummaries()
          .then((value) => {
            summaries = value;
          })
          .catch((err) => {
            summariesError = err;
          }),
      ]);

      const projects: ProjectMetadata[] = Array.isArray(projectsValue?.projects)
        ? projectsValue.projects
        : [];

      // store full metadata list for map usage
      setAllProjects(projects);

      if (projectsError && summariesError) {
        setProjectSummaries([]);
        setError(formatAxiosError(projectsError, 'Error al cargar los proyectos'));
        return;
      }

      const summariesByCode = new Map<string, ProjectSummary>(
        summaries.map((s) => [s.project_code, s] as const)
      );
      const projectCodes = new Set(projects.map((p) => p.project_code));

      const merged: ProjectCardData[] = projects.map((p) => {
        const summary = summariesByCode.get(p.project_code);
        return {
          project_code: p.project_code,
          project_name: p.project_name,
          availability_status: p.availability_status,
          currency: p.currency || 'COP',
          context_count: summary?.context_count ?? 0,
          document_count: summary?.document_count ?? 0,
          last_updated: summary?.last_updated,
          metadata_updated_at: p.updated_at,
        };
      });

      for (const summary of summaries) {
        if (!projectCodes.has(summary.project_code)) {
          merged.push({
            project_code: summary.project_code,
            context_count: summary.context_count ?? 0,
            document_count: summary.document_count ?? 0,
            last_updated: summary.last_updated,
          });
        }
      }

      if (merged.length === 0) {
        if (projectsError) {
          setError(formatAxiosError(projectsError, 'Error al cargar los proyectos'));
        } else if (summariesError) {
          setError(formatAxiosError(summariesError, 'Error al cargar los proyectos'));
        }
      }

      setProjectSummaries(merged);
    } catch (err: any) {
      console.error('Failed to load projects:', err);
      setError(formatAxiosError(err, 'Error al cargar los proyectos'));
    } finally {
      setLoading(false);
    }
	  };

  const detailsDirty = (() => {
    const initial = detailsInitialRef.current;
    if (!initial) return false;

    return (
      initial.status !== detailsForm.status ||
      initial.developer !== detailsForm.developer ||
      initial.contact !== detailsForm.contact ||
      initial.hubspot_virtual_meeting_slug !== detailsForm.hubspot_virtual_meeting_slug ||
      initial.hubspot_virtual_scheduling_enabled !== detailsForm.hubspot_virtual_scheduling_enabled ||
      initial.virtual_advisor_timezone !== detailsForm.virtual_advisor_timezone ||
      initial.plus_code !== detailsForm.plus_code ||
      initial.link !== detailsForm.link ||
      initial.pdf_link !== detailsForm.pdf_link ||
      initial.currency !== detailsForm.currency ||
      initial.location !== detailsForm.location ||
      initial.lat !== detailsForm.lat ||
      initial.lng !== detailsForm.lng
    );
  })();

  const loadProjectDetails = async (projectCode: string) => {
    setDetailsLoading(true);
    setDetailsError(null);
    setDetailsSuccess(null);
    try {
      const project = await projectService.getProject(projectCode);
      const nextForm: ProjectDetailsFormState = {
        project_name: project.project_name || '',
        status: project.status || 'active',
        developer: project.developer || '',
        contact: project.contact || '',
        hubspot_virtual_meeting_slug: project.hubspot_virtual_meeting_slug || '',
        hubspot_virtual_scheduling_enabled: project.hubspot_virtual_scheduling_enabled !== false,
        virtual_advisor_timezone: project.virtual_advisor_timezone || 'America/Bogota',
        plus_code: project.plus_code || '',
        link: project.link || '',
        pdf_link: project.pdf_link || '',
        availability_status: project.availability_status || '',
        currency: project.currency || 'COP',
        location: project.location || '',
        lat: project.lat != null ? String(project.lat) : '',
        lng: project.lng != null ? String(project.lng) : '',
      };
      detailsInitialRef.current = nextForm;
      setDetailsForm(nextForm);
    } catch (err: any) {
      setDetailsError(formatAxiosError(err, 'Error al cargar los detalles del proyecto'));
    } finally {
      setDetailsLoading(false);
    }
  };

  const applyProjectCodeChange = (projectCode: string) => {
    setSelectedProjectCode(projectCode);
    setContextFilters(prev => ({ ...prev, project_code: projectCode || undefined }));
    setDocumentFilters(prev => ({ ...prev, project_code: projectCode || undefined }));
    setDetailsError(null);
    setDetailsSuccess(null);
    detailsInitialRef.current = null;
    setAddressPreviewMessage(null);
  };

  const requestProjectCodeChange = (projectCode: string) => {
    if (detailsDirty && !detailsSaving) {
      setPendingProjectCode(projectCode);
      setDiscardDialogOpen(true);
      return;
    }
    applyProjectCodeChange(projectCode);
  };

  const requestTabChange = (newValue: number) => {
    if (detailsDirty && !detailsSaving) {
      setPendingTabValue(newValue);
      setDiscardDialogOpen(true);
      return;
    }
    setTabValue(newValue);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    requestTabChange(newValue);
  };

  const actionsMenuOpen = Boolean(actionsAnchorEl);
  const handleActionsMenuOpen = (event: React.MouseEvent<HTMLElement>) => setActionsAnchorEl(event.currentTarget);
  const handleActionsMenuClose = () => setActionsAnchorEl(null);

  const handleDiscardDialogClose = () => {
    setDiscardDialogOpen(false);
    setPendingTabValue(null);
    setPendingProjectCode(null);
  };

  const handleDiscardAndContinue = () => {
    if (detailsInitialRef.current) {
      setDetailsForm(detailsInitialRef.current);
    }
    setDetailsError(null);
    setDetailsSuccess(null);

    setDiscardDialogOpen(false);

    if (pendingProjectCode !== null) {
      const pc = pendingProjectCode;
      setPendingProjectCode(null);
      setPendingTabValue(null);
      applyProjectCodeChange(pc);
      return;
    }

    if (pendingTabValue !== null) {
      const nv = pendingTabValue;
      setPendingTabValue(null);
      setTabValue(nv);
    }
  };

  const handleRefresh = () => {
    setActionMessage(null);
    loadProjects();
    setContextFilters(prev => ({ ...prev }));
    setDocumentFilters(prev => ({ ...prev }));
    if (selectedProjectCode && tabValue === 0) {
      loadProjectDetails(selectedProjectCode);
    }
  };

  const handleSyncVectorStoreProject = async () => {
    if (!selectedProjectCode) return;
    handleActionsMenuClose();
    setActionMessage(null);
    setError(null);
    setVectorActionLoading(true);
    try {
      await projectService.syncProjectVectors(selectedProjectCode, 'incremental', false);
      setActionMessage(`Sincronización iniciada para ${selectedProjectCode}`);
    } catch (err: any) {
      setError(formatAxiosError(err, 'Error al sincronizar Vector Store'));
    } finally {
      setVectorActionLoading(false);
    }
  };

  const handleOpenVectorStorePage = () => {
    handleActionsMenuClose();
    navigate('/projects/vector-store');
  };

  const handleCreateContext = () => {
    setEditingContext(null);
    setContextFormOpen(true);
  };

  const handleEditContext = (context: ProjectContext) => {
    setEditingContext(context);
    setContextFormOpen(true);
  };

  const handleViewContext = (context: ProjectContext) => {
    // For now, just edit - could implement a read-only view later
    handleEditContext(context);
  };

  const handleContextFormClose = () => {
    setContextFormOpen(false);
    setEditingContext(null);
  };

  const handleContextFormSuccess = () => {
    handleRefresh();
    setContextFilters(prev => ({ ...prev }));
  };

  const handleEditDocument = (document: ProjectDocument) => {
    // TODO: Implement document editing dialog
    console.log('Edit document:', document);
  };

  const handlePreviewDocument = (document: ProjectDocument) => {
    setPreviewDocument(document);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewDocument(null);
  };

  const handleOpenUploadDialog = () => setUploadDialogOpen(true);
  const handleCloseUploadDialog = () => setUploadDialogOpen(false);

  const handleProjectCodeChange = (projectCode: string) => {
    requestProjectCodeChange(projectCode);
  };

  const handleOpenPdfPicker = () => {
    if (!selectedProjectCode || detailsLoading || detailsSaving || pdfUploading) return;
    pdfUploadInputRef.current?.click();
  };

  const handleProjectPdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !selectedProjectCode) return;

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setDetailsError('Solo se permiten archivos PDF');
      return;
    }

    setPdfUploading(true);
    setDetailsError(null);
    setDetailsSuccess(null);

    try {
      const result = await projectService.uploadProjectPdfLink(selectedProjectCode, file);
      const nextPdfLink = result.pdf_link || '';

      setDetailsForm((prev) => ({ ...prev, pdf_link: nextPdfLink }));
      if (detailsInitialRef.current) {
        detailsInitialRef.current = {
          ...detailsInitialRef.current,
          pdf_link: nextPdfLink,
        };
      }

      setDetailsSuccess('PDF subido y enlace actualizado');
      setProjectSummaries((prev) =>
        prev.map((p) =>
          p.project_code === selectedProjectCode
            ? {
                ...p,
                metadata_updated_at: result.updated_at ?? p.metadata_updated_at,
              }
            : p
        )
      );
      loadProjects();
    } catch (err: any) {
      setDetailsError(formatAxiosError(err, 'Error al subir PDF del proyecto'));
    } finally {
      setPdfUploading(false);
    }
  };

  const handleSaveProjectDetails = async () => {
    if (!selectedProjectCode) return;

    // Important: for nullable fields, send explicit `null` when the user clears the input.
    // `undefined` would omit the field from the payload and the backend would keep the old value.
    const normalizeNullableString = (value: string) => {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : null;
    };

    const normalizeNumber = (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const normalizeOptionalString = (value: string) => {
      const trimmed = value.trim();
      return trimmed.length ? trimmed : undefined;
    };

    const payload: ProjectMetadataUpdate = {
      status: detailsForm.status,
      developer: normalizeNullableString(detailsForm.developer),
      contact: normalizeNullableString(detailsForm.contact),
      hubspot_virtual_meeting_slug: normalizeNullableString(detailsForm.hubspot_virtual_meeting_slug),
      hubspot_virtual_scheduling_enabled: detailsForm.hubspot_virtual_scheduling_enabled,
      virtual_advisor_timezone: normalizeOptionalString(detailsForm.virtual_advisor_timezone) || 'America/Bogota',
      plus_code: normalizeNullableString(detailsForm.plus_code),
      link: normalizeNullableString(detailsForm.link),
      pdf_link: normalizeNullableString(detailsForm.pdf_link),
      currency: normalizeOptionalString(detailsForm.currency) || 'COP',
      location: normalizeOptionalString(detailsForm.location),
      lat: normalizeNumber(detailsForm.lat),
      lng: normalizeNumber(detailsForm.lng),
    };

    setDetailsSaving(true);
    setDetailsError(null);
    setDetailsSuccess(null);
    try {
      const updated = await projectService.updateProject(selectedProjectCode, payload);

      const nextForm: ProjectDetailsFormState = {
        project_name: updated.project_name || detailsForm.project_name || '',
        status: updated.status || detailsForm.status || 'active',
        developer: updated.developer || '',
        contact: updated.contact || '',
        hubspot_virtual_meeting_slug: updated.hubspot_virtual_meeting_slug || '',
        hubspot_virtual_scheduling_enabled: updated.hubspot_virtual_scheduling_enabled !== false,
        virtual_advisor_timezone: updated.virtual_advisor_timezone || 'America/Bogota',
        plus_code: updated.plus_code || '',
        link: updated.link || '',
        pdf_link: updated.pdf_link || '',
        availability_status: updated.availability_status || '',
        currency: updated.currency || 'COP',
        location: updated.location || '',
        lat: updated.lat != null ? String(updated.lat) : '',
        lng: updated.lng != null ? String(updated.lng) : '',
      };

      detailsInitialRef.current = nextForm;
      setDetailsForm(nextForm);
      setDetailsSuccess('Proyecto actualizado');

      // Update local project list immediately so "Recientemente editados" and the activity
      // stamp reflect backend timestamps without waiting for the next refresh.
      setProjectSummaries((prev) =>
        prev.map((p) =>
          p.project_code === selectedProjectCode
            ? {
                ...p,
                project_name: updated.project_name ?? p.project_name,
                availability_status: updated.availability_status ?? p.availability_status,
                currency: updated.currency ?? p.currency,
                metadata_updated_at: updated.updated_at ?? p.metadata_updated_at,
              }
            : p
        )
      );
      // update local projects list so the map reflects the new coordinates immediately
      const newLat = updated.lat;
      const newLng = updated.lng;
      setAllProjects((prev) =>
        prev.map((p) =>
          p.project_code === selectedProjectCode
              ? {
                ...p,
                status: updated.status ?? p.status,
                plus_code: updated.plus_code ?? p.plus_code,
                location: updated.location ?? p.location,
                lat: newLat != null ? newLat : p.lat,
                lng: newLng != null ? newLng : p.lng,
              }
            : p
        )
      );
      // also refresh from server in background
      loadProjects();
    } catch (err: any) {
      setDetailsError(formatAxiosError(err, 'Error al guardar el proyecto'));
    } finally {
      setDetailsSaving(false);
    }
  };

  const applyAddressPreview = (preview: ProjectAddressPreview, previewKey?: string) => {
    setDetailsForm((prev) => ({
      ...prev,
      plus_code: preview.plus_code ?? prev.plus_code,
      location: preview.location ?? prev.location,
      lat: preview.lat != null ? String(preview.lat) : prev.lat,
      lng: preview.lng != null ? String(preview.lng) : prev.lng,
    }));
  };

  const openLocationPicker = () => setLocationPickerOpen(true);
  const closeLocationPicker = () => setLocationPickerOpen(false);

  const mapsApiKey = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() || '';
  const mapsMapId = (process.env.REACT_APP_GOOGLE_MAPS_MAP_ID as string | undefined)?.trim() || '';
  const derivedPoint = useMemo(() => {
    const lat = Number(detailsForm.lat);
    const lng = Number(detailsForm.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return { lat, lng };
  }, [detailsForm.lat, detailsForm.lng]);
  const hasDerivedLocation = Boolean(derivedPoint);
  const locationButtonLabel = hasDerivedLocation ? 'Cambiar ubicación' : 'Seleccionar ubicación';

  const formatCoordForDisplay = (raw: string) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return '';
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return trimmed;
    return parsed.toFixed(4);
  };

  // Initialize/update the dialog map when opened and when projects change
  useEffect(() => {
    if (!mapDialogOpen) return;
    if (!mapsApiKey) return;

    const waitForGoogle = async (timeout = 2000) => {
      const start = Date.now();
      // eslint-disable-next-line no-async-promise-executor
      return new Promise<any>((resolve, reject) => {
        const check = () => {
          const g = (window as any).google;
          if (g && g.maps) return resolve(g);
          if (Date.now() - start > timeout) return reject(new Error('google maps not available'));
          setTimeout(check, 100);
        };
        check();
      });
    };

    let cancelled = false;

    const init = async () => {
      try {
        const g = await waitForGoogle(5000);
        if (cancelled) return;
        const container = mapContainerRef.current;
        if (!container) return;

        const projectsWithCoords = allProjects.filter(
          (p) => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng))
        );

        // clear old markers first (in case they're attached to a previous map)
        markersRef.current.forEach((m) => {
          try { m.setMap(null); } catch (e) { /* ignore */ }
        });
        markersRef.current = [];
        if (infoWindowRef.current) {
          try { infoWindowRef.current.close(); } catch (e) { /* ignore */ }
        }

        // always create a fresh native Map instance for the dialog (avoids stale DOM/map references)
        googleMapRef.current = new g.maps.Map(container, {
          center: { lat: 0, lng: 0 },
          zoom: 2,
          mapId: mapsMapId || undefined,
        });

        if (!infoWindowRef.current) infoWindowRef.current = new g.maps.InfoWindow();

        if (projectsWithCoords.length === 0) {
          googleMapRef.current.setCenter({ lat: 0, lng: 0 });
          googleMapRef.current.setZoom(2);
          return;
        }

        const bounds = new g.maps.LatLngBounds();
        projectsWithCoords.forEach((p) => {
          const pos = { lat: Number(p.lat), lng: Number(p.lng) };
          const marker = new g.maps.Marker({ position: pos, map: googleMapRef.current, title: p.project_name || p.project_code });
          marker.addListener('click', () => {
            const title = escapeHtml(p.project_name || p.project_code);
            const code = escapeHtml(p.project_code);
            const container = document.createElement('div');
            container.style.minWidth = '200px';
            container.innerHTML = `\n              <div style="font-family: Roboto, Arial, sans-serif;">\n                <strong style="font-size:14px">${title}</strong>\n                <div style="font-size:12px;color:#666;margin-top:4px">${code}</div>\n                <div style="margin-top:8px;text-align:right">\n                  <button id="open-proj-${code}" type="button" style="padding:6px 8px;cursor:pointer">Ver proyecto</button>\n                </div>\n              </div>\n            `;
            infoWindowRef.current.setContent(container);
            infoWindowRef.current.open({ map: googleMapRef.current, anchor: marker });
            setTimeout(() => {
              const btn = document.getElementById(`open-proj-${code}`);
              if (btn) {
                btn.addEventListener('click', () => {
                  setSelectedProjectCode(p.project_code);
                  setTabValue(0);
                  loadProjectDetails(p.project_code);
                  setMapDialogOpen(false);
                });
              }
            }, 0);
          });
          markersRef.current.push(marker);
          bounds.extend(pos as any);
        });

        if (projectsWithCoords.length === 1) {
          googleMapRef.current.setCenter(bounds.getCenter());
          googleMapRef.current.setZoom(15);
        } else {
          googleMapRef.current.fitBounds(bounds);
        }

        const resizeObserver = new ResizeObserver(() => {
          if (googleMapRef.current) {
            g.maps.event.trigger(googleMapRef.current, 'resize');
            if (projectsWithCoords.length > 1) googleMapRef.current.fitBounds(bounds);
          }
        });
        resizeObserver.observe(container);

        return () => resizeObserver.disconnect();
      } catch (err) {
        // failed to load google maps script in time
        console.warn('Google Maps not ready:', err);
      }
    };

    const cleanupPromise = init();
    return () => {
      cancelled = true;
      // no reliable way to cancel the async wait; just clear refs
      try { markersRef.current.forEach((m) => m.setMap(null)); } catch (e) {}
      markersRef.current = [];
      if (infoWindowRef.current) {
        try { infoWindowRef.current.close(); } catch (e) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapDialogOpen, allProjects, mapsApiKey, mapsMapId]);

  

  const getContextTypeLabel = (contextType: string) => {
    const labels: { [key: string]: string } = {
      description: 'Descripción',
      amenities: 'Amenidades',
      location_details: 'Detalles de Ubicación',
      pricing: 'Precios',
      specifications: 'Especificaciones',
      gallery: 'Galería',
      contact_info: 'Información de Contacto',
      custom: 'Personalizado'
    };
    return labels[contextType] || contextType;
  };

  const getDocumentTypeLabel = (documentType: string) => {
    const labels: { [key: string]: string } = {
      brochure: 'Brochure',
      floor_plan: 'Plano',
      additional_info: 'Información Adicional',
      image: 'Imagen',
      video: 'Video',
      document: 'Documento'
    };
    return labels[documentType] || documentType;
  };

  return (
    <Box className="fade-in">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Gestión de Proyectos
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Administra los contextos y documentos de los proyectos inmobiliarios
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <ButtonGroup variant="outlined" disabled={loading || vectorActionLoading}>
            <Button startIcon={<RefreshIcon />} onClick={handleRefresh}>
              Recargar
            </Button>
            <Button size="small" onClick={handleActionsMenuOpen} aria-label="Más acciones">
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>
          <Menu
            anchorEl={actionsAnchorEl}
            open={actionsMenuOpen}
            onClose={handleActionsMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleSyncVectorStoreProject} disabled={!selectedProjectCode || vectorActionLoading}>
              <SyncIcon fontSize="small" style={{ marginRight: 8 }} />
              Sincronizar Vector Store (proyecto)
            </MenuItem>
            {/* Recalcular ubicación removed */}
            <Divider />
            <MenuItem onClick={handleOpenVectorStorePage}>
              <SyncIcon fontSize="small" style={{ marginRight: 8 }} />
              Abrir Vector Store
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {actionMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setActionMessage(null)}>
          {actionMessage}
        </Alert>
      )}

      {/* Recently edited projects */}
      {projectSummaries.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight="bold">Recientemente editados</Typography>
            </Box>
          </Box>

          <Grid container spacing={2}>
            {recentProjects.map((summary) => (
              <Grid item xs={12} sm={6} md={3} key={summary.project_code}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                    border: selectedProjectCode === summary.project_code ? 2 : 0,
                    borderColor: 'primary.main',
                    position: 'relative',
                    height: 180,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                  onClick={() => handleProjectCodeChange(selectedProjectCode === summary.project_code ? '' : summary.project_code)}
                >
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Typography variant="h6" gutterBottom sx={{ mr: 1 }}>
                        {summary.project_name ?? summary.project_code}
                      </Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="body2" color="text.secondary">Contextos:</Typography>
                      <Chip label={summary.context_count} size="small" color="primary" />
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Documentos:</Typography>
                      <Chip label={summary.document_count} size="small" color="secondary" />
                    </Box>
                    <Box sx={{ mt: 1 }}>
                      {(() => {
                        const activityMs = Math.max(
                          parseTime(summary.last_updated),
                          parseTime(summary.metadata_updated_at)
                        );

                        if (!activityMs) {
                          // reserve same space when there's no activity so cards maintain equal height
                          return (
                            <Typography variant="caption" color="text.secondary" sx={{ visibility: 'hidden', display: 'block' }}>
                              Última actividad: -
                            </Typography>
                          );
                        }

                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Última actividad: {new Date(activityMs).toLocaleString()}
                          </Typography>
                        );
                      })()}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Proyecto */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={9}>
            <Autocomplete<ProjectCardData, false, false, false>
              options={projectDropdownOptions}
              value={projectSummaries.find((p) => p.project_code === selectedProjectCode) ?? null}
              onChange={(event, newValue) => handleProjectCodeChange(newValue?.project_code ?? '')}
              getOptionLabel={(option) => `${option.project_name!} (${option.project_code})`}
              isOptionEqualToValue={(option, value) => option.project_code === value.project_code}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Proyecto"
                  placeholder="Seleccionar proyecto"
                  size="small"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box
              component="a"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                if (projectSummaries.length === 0) return;
                setMapDialogOpen(true);
              }}
              sx={{
                display: 'block',
                width: '100%',
                height: 70,
                borderRadius: 1,
                overflow: 'hidden',
                textDecoration: 'none',
                backgroundImage: `url('/images/map-banner.svg')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                boxShadow: 1,
                transition: 'transform .18s, box-shadow .18s',
                '&:hover': { transform: 'scale(1.01)', boxShadow: 6 },
                opacity: projectSummaries.length === 0 ? 0.6 : 1,
                pointerEvents: projectSummaries.length === 0 ? 'none' : 'auto',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.18)',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 3,
                    py: 1.25,
                    bgcolor: '#0b4f86',
                    color: 'common.white',
                    borderRadius: '999px',
                    boxShadow: '0 3px 8px rgba(0,0,0,0.25)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    cursor: projectSummaries.length === 0 ? 'default' : 'pointer',
                  }}
                >
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                    Mostrar mapa
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Detalles" disabled={!selectedProjectCode} />
          <Tab label="Contextos" />
          <Tab label="Documentos" disabled={!selectedProjectCode} />
        </Tabs>
          <TabPanel value={tabValue} index={0}>
            {!selectedProjectCode ? (
              <Alert severity="info">Selecciona un proyecto para editar sus detalles</Alert>
            ) : (
              <Box sx={{ p: 2 }}>
                {detailsError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDetailsError(null)}>
                    {detailsError}
                  </Alert>
                )}
                {detailsSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDetailsSuccess(null)}>
                    {detailsSuccess}
                  </Alert>
                )}
                {detailsLoading && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Cargando detalles...
                  </Alert>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Proyecto
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={detailsForm.status === 'active'}
                                onChange={(e) =>
                                  setDetailsForm(prev => ({
                                    ...prev,
                                    status: e.target.checked ? 'active' : 'inactive',
                                  }))
                                }
                                disabled={detailsLoading || detailsSaving}
                              />
                            }
                            label="Ofrecer este proyecto a clientes"
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Al apagarlo, Sara dejará de ofrecer el proyecto. El inventario seguirá sincronizándose y podrás activarlo de nuevo.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Código" size="small" value={selectedProjectCode} disabled />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField fullWidth label="Nombre" size="small" value={detailsForm.project_name} disabled />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Disponibilidad"
                            size="small"
                            value={detailsForm.availability_status}
                            disabled
                          />
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <TextField
                            fullWidth
                            label="Moneda"
                            size="small"
                            value={detailsForm.currency}
                            onChange={(e) => setDetailsForm(prev => ({ ...prev, currency: e.target.value }))}
                            disabled={detailsLoading || detailsSaving}
                            inputProps={{ maxLength: 32 }}
                            helperText="Ej: COP, USD"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Desarrollador"
                            size="small"
                            value={detailsForm.developer}
                            onChange={(e) => setDetailsForm(prev => ({ ...prev, developer: e.target.value }))}
                            disabled={detailsLoading || detailsSaving}
                            inputProps={{ maxLength: 255 }}
                            helperText="Se usará en los mensajes de reenganche"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Contacto"
                            size="small"
                            value={detailsForm.contact}
                            onChange={(e) => setDetailsForm(prev => ({ ...prev, contact: e.target.value }))}
                            disabled={detailsLoading || detailsSaving}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Slug HubSpot cita virtual"
                            size="small"
                            value={detailsForm.hubspot_virtual_meeting_slug}
                            onChange={(e) => setDetailsForm(prev => ({ ...prev, hubspot_virtual_meeting_slug: e.target.value }))}
                            disabled={detailsLoading || detailsSaving || !detailsForm.hubspot_virtual_scheduling_enabled}
                            helperText="Ej: asesor/proyecto o URL de Meetings"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={detailsForm.hubspot_virtual_scheduling_enabled}
                                onChange={(e) => setDetailsForm(prev => ({ ...prev, hubspot_virtual_scheduling_enabled: e.target.checked }))}
                                disabled={detailsLoading || detailsSaving}
                              />
                            }
                            label="Habilitar citas virtuales"
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Si está apagado, no se ofrecerán citas virtuales aunque exista un slug.
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Zona horaria asesor virtual"
                            size="small"
                            value={detailsForm.virtual_advisor_timezone}
                            onChange={(e) => setDetailsForm(prev => ({ ...prev, virtual_advisor_timezone: e.target.value }))}
                            disabled={detailsLoading || detailsSaving || !detailsForm.hubspot_virtual_scheduling_enabled}
                            helperText="IANA, ej: America/Bogota o America/Santo_Domingo"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Enlaces
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Link"
                            size="small"
                            value={detailsForm.link}
                            onChange={(e) => setDetailsForm(prev => ({ ...prev, link: e.target.value }))}
                            disabled={detailsLoading || detailsSaving || pdfUploading}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                              fullWidth
                              label="PDF Link"
                              size="small"
                              value={detailsForm.pdf_link}
                              onChange={(e) => setDetailsForm(prev => ({ ...prev, pdf_link: e.target.value }))}
                              disabled={detailsLoading || detailsSaving || pdfUploading}
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={handleOpenPdfPicker}
                              startIcon={<UploadFileIcon />}
                              disabled={!selectedProjectCode || detailsLoading || detailsSaving || pdfUploading}
                              sx={{ flexShrink: 0, minWidth: 130 }}
                            >
                              {pdfUploading ? 'Subiendo...' : 'Subir PDF'}
                            </Button>
                            <input
                              ref={pdfUploadInputRef}
                              type="file"
                              accept=".pdf,application/pdf"
                              style={{ display: 'none' }}
                              onChange={handleProjectPdfUpload}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2">Ubicación</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={openLocationPicker}
                          disabled={!selectedProjectCode || detailsLoading || detailsSaving}
                        >
                          {locationButtonLabel}
                        </Button>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                          <Box
                            sx={{
                              height: 260,
                              width: '100%',
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid',
                              borderColor: 'divider',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: derivedPoint ? 'transparent' : 'action.hover',
                              cursor: mapsApiKey ? 'pointer' : 'default',
                            }}
                            onClick={() => {
                              if (mapsApiKey && selectedProjectCode && !detailsLoading && !detailsSaving) openLocationPicker();
                            }}
                          >
                            {!mapsApiKey ? (
                              <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                                Mapa no disponible: falta configurar Google Maps para el frontend.
                              </Typography>
                            ) : !derivedPoint ? (
                              <Typography variant="body2" color="text.secondary" sx={{ px: 2, textAlign: 'center' }}>
                                Sin coordenadas guardadas. Usa “{locationButtonLabel}” para definirla.
                              </Typography>
                            ) : (
                              <APIProvider
                                apiKey={mapsApiKey}
                                language="es"
                                libraries={mapsMapId ? ['marker'] : []}
                              >
                                <GoogleMap
                                  center={derivedPoint}
                                  zoom={16}
                                  style={{ width: '100%', height: '100%' }}
                                  disableDefaultUI
                                  gestureHandling="none"
                                  mapId={mapsMapId || undefined}
                                >
                                  {mapsMapId ? (
                                    <AdvancedMarker position={derivedPoint}>
                                      <Pin />
                                    </AdvancedMarker>
                                  ) : (
                                    <Marker position={derivedPoint} />
                                  )}
                                </GoogleMap>
                              </APIProvider>
                            )}
                          </Box>
                        </Grid>

                        <Grid item xs={12} md={5}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Ubicación"
                                size="small"
                                value={detailsForm.location}
                                InputProps={{ readOnly: true }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                label="Plus Code"
                                size="small"
                                value={detailsForm.plus_code}
                                InputProps={{ readOnly: true }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Latitud"
                                type="number"
                                size="small"
                                value={formatCoordForDisplay(detailsForm.lat)}
                                InputProps={{ readOnly: true }}
                                disabled
                              />
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Longitud"
                                type="number"
                                size="small"
                                value={formatCoordForDisplay(detailsForm.lng)}
                                InputProps={{ readOnly: true }}
                                disabled
                              />
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      onClick={handleSaveProjectDetails}
                      disabled={detailsLoading || detailsSaving}
                    >
                      {detailsSaving ? 'Guardando...' : 'Guardar cambios'}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <Box display="flex" justifyContent="flex-end" mb={2} sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateContext}
                disabled={!selectedProjectCode}
              >
                Nuevo Contexto
              </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Tipo de Contexto</InputLabel>
                    <Select
                      value={contextFilters.context_type || ''}
                      onChange={(e) =>
                        setContextFilters(prev => ({
                          ...prev,
                          context_type: e.target.value || undefined,
                        }))
                      }
                      label="Tipo de Contexto"
                    >
                      <MenuItem value="">Todos</MenuItem>
                      {Object.values(CONTEXT_TYPES).map((type) => (
                        <MenuItem key={type} value={type}>
                          {getContextTypeLabel(type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Buscar"
                    placeholder="Buscar en título o contenido..."
                    value={contextFilters.search_query || ''}
                    onChange={(e) =>
                      setContextFilters(prev => ({
                        ...prev,
                        search_query: e.target.value || undefined,
                      }))
                    }
                    InputProps={{
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() =>
                      setContextFilters({
                        project_code: selectedProjectCode || undefined,
                      })
                    }
                  >
                    Limpiar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
            {!selectedProjectCode && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Selecciona un proyecto para crear contextos o administrar documentos.
              </Alert>
            )}
            <ProjectContextList
              filters={contextFilters}
              onEditContext={handleEditContext}
              onViewContext={handleViewContext}
              onRefresh={handleRefresh}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box display="flex" justifyContent="flex-end" mb={2} sx={{ pr: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenUploadDialog}
                disabled={!selectedProjectCode}
              >
                Subir Documento
              </Button>
            </Box>
            <DocumentList
              filters={documentFilters}
              onEditDocument={handleEditDocument}
              onRefresh={handleRefresh}
            />
          </TabPanel>

      </Paper>

      {/* Map dialog showing all projects */}
      <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Mapa de proyectos</DialogTitle>
        <DialogContent dividers sx={{ height: 600, p: 0 }}>
          {!mapsApiKey ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">Mapa no disponible: falta configurar Google Maps para el frontend.</Typography>
            </Box>
          ) : (
            (() => {
              const projectsWithCoords = allProjects.filter(p => Number.isFinite(Number(p.lat)) && Number.isFinite(Number(p.lng)));
              if (projectsWithCoords.length === 0) {
                return (
                  <Box sx={{ p: 3 }}>
                    <Typography color="text.secondary">No hay coordenadas disponibles en los proyectos para mostrar en el mapa.</Typography>
                  </Box>
                );
              }

              const first = projectsWithCoords[0];
              const center = { lat: Number(first.lat), lng: Number(first.lng) };

              return (
                <APIProvider apiKey={mapsApiKey} language="es" libraries={mapsMapId ? ['marker'] : []}>
                  <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
                </APIProvider>
              );
            })()
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <LocationPickerDialog
        open={locationPickerOpen}
        projectCode={selectedProjectCode}
        currentLat={detailsForm.lat ? Number(detailsForm.lat) : undefined}
        currentLng={detailsForm.lng ? Number(detailsForm.lng) : undefined}
        onClose={closeLocationPicker}
        onApply={(preview, previewKey) => {
          applyAddressPreview(preview, previewKey);
          setAddressPreviewMessage('Dirección y coordenadas guardadas automáticamente');
          closeLocationPicker();
        }}
      />

      <Dialog open={uploadDialogOpen} onClose={handleCloseUploadDialog} fullWidth maxWidth="md">
	        <DialogTitle>Subir documento ({selectedProjectCode || '-'})</DialogTitle>
	        <DialogContent dividers>
	          {selectedProjectCode ? (
	            <DocumentUpload
	              projectCode={selectedProjectCode}
	              currentUser={user?.username || 'unknown'}
	              onUploadSuccess={() => {
	                handleCloseUploadDialog();
	                setDocumentFilters(prev => ({ ...prev }));
                  handleRefresh();
	              }}
	            />
	          ) : (
	            <Alert severity="info">Selecciona un proyecto para subir documentos</Alert>
	          )}
	        </DialogContent>
	        <DialogActions>
	          <Button onClick={handleCloseUploadDialog}>Cerrar</Button>
	        </DialogActions>
	      </Dialog>

	      <Dialog open={discardDialogOpen} onClose={handleDiscardDialogClose} maxWidth="sm" fullWidth>
	        <DialogTitle>Cambios sin guardar</DialogTitle>
	        <DialogContent dividers>
	          <Alert severity="warning">
	            Tienes cambios sin guardar en "Detalles". ¿Quieres descartarlos y continuar?
	          </Alert>
	        </DialogContent>
	        <DialogActions>
	          <Button onClick={handleDiscardDialogClose}>Cancelar</Button>
	          <Button onClick={handleDiscardAndContinue} variant="contained" color="warning">
	            Descartar cambios
	          </Button>
	        </DialogActions>
	      </Dialog>

	      {/* Context Form Dialog */}
	      <ProjectContextForm
	        open={contextFormOpen}
        onClose={handleContextFormClose}
        onSuccess={handleContextFormSuccess}
        context={editingContext}
        defaultProjectCode={selectedProjectCode}
        currentUser={user?.username || 'unknown'}
      />

      {/* Document Preview Dialog */}
      <DocumentPreview
        open={previewOpen}
        onClose={handlePreviewClose}
        document={previewDocument}
      />
    </Box>
  );
};

export default Projects;
